import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Megaphone } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { JsonLd } from "@/components/JsonLd";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const GoogleAdsSpringPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  const canonicalUrl = generateCanonicalUrl('/google-ads-spring');

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Google Ads en Spring TX",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": { "@type": "City", "name": "Spring", "containedIn": { "@type": "State", "name": "Texas" } }
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>Google Ads Spring TX | Campañas PPC Locales – Luis ROC</title>
        <meta name="description" content="Campañas Google Ads en Spring, TX con segmentación local precisa. Activa demanda mediante campañas pagadas con medición orientada a negocio. Reunión inicial gratuita." />
        <meta name="keywords" content="google ads spring tx, ppc spring, publicidad google spring texas" />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="geo.placename" content="Spring" />
      </Helmet>

      <JsonLd data={serviceSchema} />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main className="container mx-auto px-4 py-8">
          <BreadcrumbNav 
            items={[
              { name: "Servicios", url: "/servicios" },
              { name: "Localidades", url: "/servicios-locales-houston" },
              { name: "Google Ads Spring", url: "/google-ads-spring" }
            ]}
            language="es"
          />

          <section className="py-16 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              Google Ads en Spring TX — Campañas orientadas a demanda local
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              Activa demanda mediante campañas pagadas en Spring con campañas Google Ads optimizadas para tu código postal. 
              Segmentación precisa por ZIP 77373, 77379, 77386, 77388, 77389. Estructura de campañas medible y reportes transparentes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => window.location.href = '/contacto'}>
                Reunión inicial gratuita de 30 minutos
              </Button>
            </div>
          </section>

          <section className="py-16 bg-accent/5 rounded-lg mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">Ventajas de PPC en Spring</h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <Card className="p-6">
                <Megaphone className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-semibold mb-2">Activación de demanda</h3>
                <p className="text-muted-foreground">Las campañas pagadas permiten activar demanda mientras se mide el rendimiento desde el inicio.</p>
              </Card>

              <Card className="p-6">
                <Check className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-semibold mb-2">Control de presupuesto</h3>
                <p className="text-muted-foreground">Ajustamos pujas y presupuesto según tus metas.</p>
              </Card>
            </div>
          </section>

          <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center">
            <h2 className="text-3xl font-bold mb-4">Revisa tu estrategia de Google Ads en Spring</h2>
            <Button size="lg" variant="secondary" onClick={() => window.location.href = '/contacto'}>
              Solicitar Consulta
            </Button>
          </section>
        </main>

        <KnowledgeConnections path="/google-ads-spring" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default GoogleAdsSpringPage;
